import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Quote, Eraser } from "lucide-react";

const SANITIZE = { USE_PROFILES: { html: true }, ADD_ATTR: ["target", "rel"] };
export const cleanHtml = (html) => DOMPurify.sanitize(html || "", SANITIZE);
export const isHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(s || "");

// Legacy "## heading / - bullet" plain text → HTML so older content keeps rendering
export function legacyToHtml(body) {
  if (!body) return "";
  if (isHtml(body)) return body;
  const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return body.split(/\n\s*\n/).map((b) => {
    const lines = b.trim().split("\n");
    if (!lines[0]) return "";
    if (lines[0].startsWith("## ")) return `<h2>${esc(lines[0].slice(3))}</h2>${lines.length > 1 ? `<p>${esc(lines.slice(1).join(" "))}</p>` : ""}`;
    if (lines[0].startsWith("### ")) return `<h3>${esc(lines[0].slice(4))}</h3>${lines.length > 1 ? `<p>${esc(lines.slice(1).join(" "))}</p>` : ""}`;
    if (lines.every((l) => l.startsWith("- "))) return `<ul>${lines.map((l) => `<li>${esc(l.slice(2)).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</li>`).join("")}</ul>`;
    return `<p>${esc(lines.join(" ")).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")}</p>`;
  }).join("");
}

const TOOLS = [
  ["bold", Bold, "Bold"], ["italic", Italic, "Italic"], ["underline", Underline, "Underline"],
  ["h2", Heading2, "Heading"], ["h3", Heading3, "Sub-heading"], ["ul", List, "Bullet list"], ["ol", ListOrdered, "Numbered list"],
  ["quote", Quote, "Quote"], ["link", LinkIcon, "Link"], ["clear", Eraser, "Clear formatting"],
];

export function RichEditor({ value, onChange, rows = 12, placeholder = "Start writing…", testId = "rich-editor" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "") && document.activeElement !== ref.current) ref.current.innerHTML = cleanHtml(legacyToHtml(value));
  }, [value]);
  const run = (cmd) => {
    ref.current?.focus();
    if (cmd === "h2" || cmd === "h3") document.execCommand("formatBlock", false, cmd);
    else if (cmd === "quote") document.execCommand("formatBlock", false, "blockquote");
    else if (cmd === "ul") document.execCommand("insertUnorderedList");
    else if (cmd === "ol") document.execCommand("insertOrderedList");
    else if (cmd === "clear") { document.execCommand("removeFormat"); document.execCommand("formatBlock", false, "p"); }
    else if (cmd === "link") { const url = window.prompt("Link URL (https://…)"); if (url) document.execCommand("createLink", false, url); }
    else document.execCommand(cmd);
    onChange(cleanHtml(ref.current.innerHTML));
  };
  return (
    <div className="rounded-xl border border-gold/30 bg-white/70" data-testid={testId}>
      <div className="flex flex-wrap gap-0.5 border-b border-gold/20 p-1">
        {TOOLS.map(([cmd, Icon, label]) => (
          <button key={cmd} type="button" title={label} onMouseDown={(e) => { e.preventDefault(); run(cmd); }} className="flex h-8 w-8 items-center justify-center rounded-md text-crimson hover:bg-gold-light" data-testid={`${testId}-${cmd}`}><Icon size={15} /></button>
        ))}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning role="textbox" aria-multiline="true" data-placeholder={placeholder}
        onInput={() => onChange(cleanHtml(ref.current.innerHTML))}
        onPaste={(e) => { e.preventDefault(); document.execCommand("insertHTML", false, cleanHtml(e.clipboardData.getData("text/html") || e.clipboardData.getData("text/plain").replace(/\n/g, "<br>"))); }}
        className="rich-content prose max-w-none p-3 text-sm outline-none focus:ring-2 focus:ring-gold/40 prose-headings:font-serif-display prose-headings:text-crimson"
        style={{ minHeight: `${rows * 1.6}rem` }} data-testid={`${testId}-input`} />
    </div>
  );
}

export function RichView({ html, className = "", testId }) {
  return <div className={`prose max-w-none prose-headings:font-serif-display prose-headings:text-crimson prose-a:text-palm ${className}`} data-testid={testId} dangerouslySetInnerHTML={{ __html: cleanHtml(legacyToHtml(html)) }} />;
}
